import { useRef, useState, useEffect, useMemo } from 'react';
import { EMPTY_TEXT } from "../../common";
import { ImageUploadProgressBarConstants, ImageUploadProgressBarPhase, IntelligentAssignmentSummary } from '../constants';
import { calculateProgressSummary, calculateUploadStatus, isAnalyzingIterations, isIterationsPendingOrInProgress } from '../utilities';
import { IntelligentPhotoCaptureAttachmentIterationStatus } from '../enums';
export const useImageUploadProgressBar = ({ attachmentBatchActivityInfo, attachmentIterationInfo, organizingAnalyzingMessage, organizingAnalyzingCompleteMessage, uploadingProgressText, uploadStatusIntervalMs }) => {
    const [displayedProgress, setDisplayedProgress] = useState(0);
    const [phase, setPhase] = useState(ImageUploadProgressBarPhase.Idle);
    const phaseRef = useRef(ImageUploadProgressBarPhase.Idle);
    const organizingTimeoutRef = useRef(null);
    const uploadProgressTargetRef = useRef(0);
    phaseRef.current = phase;
    const batchAttachmentCountById = useMemo(() => {
        return new Map(attachmentBatchActivityInfo.map(batchInfo => [batchInfo.batchId, batchInfo.attachments.length]));
    }, [attachmentBatchActivityInfo]);
    const progressSummary = useMemo(() => calculateProgressSummary(attachmentIterationInfo, batchAttachmentCountById), [attachmentIterationInfo, batchAttachmentCountById]);
    const hasPendingOrInProgressIteration = useMemo(() => isIterationsPendingOrInProgress(attachmentIterationInfo), [attachmentIterationInfo]);
    const hasAnalyzingIteration = useMemo(() => isAnalyzingIterations(attachmentIterationInfo), [
        attachmentIterationInfo
    ]);
    const isRenderingPending = useMemo(() => attachmentIterationInfo.some(iteration => iteration.iterationStatus === IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED &&
        !iteration.isRenderingCompleted &&
        iteration.batchIds.length > 0), [attachmentIterationInfo]);
    // Organizing/Analyzing is active if either Analyze API or rendering is pending
    const isOrganizingAnalyzingActive = hasAnalyzingIteration || isRenderingPending;
    // Utility for timer cleanup (for coverage and DRYness)
    function cleanupOrganizingTimeout() {
        if (organizingTimeoutRef.current !== null) {
            clearTimeout(organizingTimeoutRef.current);
            organizingTimeoutRef.current = null;
        }
    }
    // Track if we've ever entered Organizing/Analyzing in this session
    const hasEverOrganizedRef = useRef(false);
    // Track if we are in the "Complete" timeout
    const completeTimeoutActiveRef = useRef(false);
    // Track if Complete has been shown this session
    const hasShownCompleteThisSessionRef = useRef(false);
    useEffect(() => {
        return () => {
            cleanupOrganizingTimeout();
        };
    }, []);
    // State machine for progress bar phase using enum
    useEffect(() => {
        const currentPhase = phase;
        // If a new session starts during Complete timeout, restart in the correct phase.
        if ((hasPendingOrInProgressIteration || isOrganizingAnalyzingActive) && completeTimeoutActiveRef.current) {
            cleanupOrganizingTimeout();
            completeTimeoutActiveRef.current = false;
            hasShownCompleteThisSessionRef.current = false;
            if (hasPendingOrInProgressIteration && !isOrganizingAnalyzingActive) {
                hasEverOrganizedRef.current = false;
                setPhase(ImageUploadProgressBarPhase.Uploading);
                setDisplayedProgress(0);
                return;
            }
            setPhase(ImageUploadProgressBarPhase.Organizing);
            setDisplayedProgress(ImageUploadProgressBarConstants.HUNDRED_PERCENT);
            return;
        }
        // Show Uploading ONLY if uploading is in progress, organizing/analyzing is NOT active, and organizing/analyzing has never started in this session
        if (hasPendingOrInProgressIteration && !isOrganizingAnalyzingActive && !hasEverOrganizedRef.current) {
            if (currentPhase !== ImageUploadProgressBarPhase.Uploading) {
                setPhase(ImageUploadProgressBarPhase.Uploading);
                setDisplayedProgress(0);
            }
            cleanupOrganizingTimeout();
            return;
        }
        // If either uploading or organizing/analyzing is in progress (and we've ever organized/analyzed), stay in Organizing & Analyzing
        if (hasPendingOrInProgressIteration || isOrganizingAnalyzingActive) {
            if (!hasEverOrganizedRef.current) {
                hasEverOrganizedRef.current = true;
            }
            if (currentPhase !== ImageUploadProgressBarPhase.Organizing) {
                setPhase(ImageUploadProgressBarPhase.Organizing);
                setDisplayedProgress(ImageUploadProgressBarConstants.HUNDRED_PERCENT);
            }
            cleanupOrganizingTimeout();
            return;
        }
        // Only show Complete if we've ever organized/analyzed and everything is done, and not already shown this session
        if (hasEverOrganizedRef.current &&
            !hasPendingOrInProgressIteration &&
            !isOrganizingAnalyzingActive &&
            currentPhase === ImageUploadProgressBarPhase.Organizing &&
            !hasShownCompleteThisSessionRef.current) {
            setPhase(ImageUploadProgressBarPhase.Complete);
            completeTimeoutActiveRef.current = true;
            hasShownCompleteThisSessionRef.current = true;
            organizingTimeoutRef.current = setTimeout(() => {
                organizingTimeoutRef.current = null;
                setPhase(ImageUploadProgressBarPhase.Idle);
                hasEverOrganizedRef.current = false;
                completeTimeoutActiveRef.current = false;
                hasShownCompleteThisSessionRef.current = false;
            }, IntelligentAssignmentSummary.ORGANIZING_COMPLETE_TIMEOUT_MS);
            return;
        }
        // If already in Complete or Idle, do nothing
        if (currentPhase === ImageUploadProgressBarPhase.Complete || currentPhase === ImageUploadProgressBarPhase.Idle) {
            return;
        }
        // Default: go to Idle and reset session flag
        setPhase(ImageUploadProgressBarPhase.Idle);
        hasEverOrganizedRef.current = false;
        completeTimeoutActiveRef.current = false;
        hasShownCompleteThisSessionRef.current = false;
    }, [hasPendingOrInProgressIteration, isOrganizingAnalyzingActive, phase]);
    // Compute upload status for the progress bar
    const uploadStatus = useMemo(() => {
        if (phase === ImageUploadProgressBarPhase.Uploading) {
            return calculateUploadStatus(progressSummary, false, true, false, organizingAnalyzingMessage, uploadingProgressText);
        }
        if (phase === ImageUploadProgressBarPhase.Organizing) {
            return {
                showProgressStatus: true,
                message: organizingAnalyzingMessage,
                progress: ImageUploadProgressBarConstants.HUNDRED_PERCENT,
                isProgressPercentageDisplayed: false
            };
        }
        if (phase === ImageUploadProgressBarPhase.Complete) {
            return {
                showProgressStatus: true,
                message: organizingAnalyzingCompleteMessage,
                progress: ImageUploadProgressBarConstants.HUNDRED_PERCENT,
                isProgressPercentageDisplayed: false
            };
        }
        return {
            showProgressStatus: false,
            message: EMPTY_TEXT,
            progress: 0,
            isProgressPercentageDisplayed: false
        };
    }, [phase, progressSummary, organizingAnalyzingMessage, organizingAnalyzingCompleteMessage, uploadingProgressText]);
    uploadProgressTargetRef.current = uploadStatus.progress;
    useEffect(() => {
        let progressTimer = null;
        if (phase === ImageUploadProgressBarPhase.Uploading) {
            if (displayedProgress !== uploadStatus.progress) {
                progressTimer = setInterval(() => {
                    setDisplayedProgress(prev => {
                        if (phaseRef.current !== ImageUploadProgressBarPhase.Uploading) {
                            return prev;
                        }
                        const targetProgress = uploadProgressTargetRef.current;
                        const diff = targetProgress - prev;
                        const step = Math.ceil(Math.abs(diff) / ImageUploadProgressBarConstants.PROGRESS_SMOOTHING_DIVISOR);
                        if (diff > 0) {
                            return Math.min(targetProgress, prev + step);
                        }
                        else {
                            return Math.max(targetProgress, prev - step);
                        }
                    });
                }, uploadStatusIntervalMs);
            }
        }
        else if (phase === ImageUploadProgressBarPhase.Organizing || phase === ImageUploadProgressBarPhase.Complete) {
            if (displayedProgress !== ImageUploadProgressBarConstants.HUNDRED_PERCENT) {
                setDisplayedProgress(ImageUploadProgressBarConstants.HUNDRED_PERCENT);
            }
        }
        else if (phase === ImageUploadProgressBarPhase.Idle) {
            if (displayedProgress !== 0) {
                setDisplayedProgress(0);
            }
        }
        return () => {
            if (progressTimer) {
                clearInterval(progressTimer);
            }
        };
    }, [phase, uploadStatus.progress, uploadStatusIntervalMs, displayedProgress]);
    return {
        uploadStatus,
        displayedProgress
    };
};
//# sourceMappingURL=useImageUploadProgressBar.js.map